import React from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from "@mui/material/";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { strings } from "../../../../common/i18n";
import styles from "./styles";

/**
 * Pending Count Approval Dialog
 *
 * Shown once per action type, at the first approve or reject, while an audit is
 * still in Pending Count. The controller has to acknowledge that the count is
 * unfinished before the action is processed.
 *
 * Deliberately not dismissible by backdrop click or Escape: the ticket calls for
 * an explicit acknowledgement, so the only ways out are Cancel (abandons the
 * action) and Continue (acknowledges, then the action proceeds).
 *
 * Shared by approve and reject — `action` picks the wording. Rejecting also
 * reissues the asset before the auditor's device has synced, so that path gets
 * an extra line the approve path doesn't need.
 */
export default ({ open, action, counts, onCancel, onConfirm, loading }) => {
    if (!counts) return null;

    const { counted, total } = counts;
    const isReject = action === "reject";

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            // No onClose handler: backdrop and Escape must not dismiss this.
            aria-labelledby="pending-count-approval-title"
        >
            <DialogTitle id="pending-count-approval-title" sx={styles.title}>
                <WarningAmberOutlinedIcon sx={styles.titleIcon} />
                {strings("messages.pendingCountApprovalTitle")}
            </DialogTitle>
            <DialogContent dividers>
                <DialogContentText component="div">
                    {strings("messages.pendingCountApprovalBody", {
                        counted,
                        total
                    })}
                </DialogContentText>
                {isReject && (
                    <DialogContentText component="div">
                        {strings("messages.pendingCountRejectionExtra")}
                    </DialogContentText>
                )}
                <DialogContentText sx={styles.ask}>
                    {strings(
                        isReject
                            ? "messages.pendingCountRejectionAsk"
                            : "messages.pendingCountApprovalAsk"
                    )}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={styles.actions}>
                <Button onClick={onCancel} disabled={loading}>
                    {strings("cancel")}
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    disabled={loading}
                >
                    {strings("messages.pendingCountApprovalConfirm")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
